/**
 * `useChatModule` — the chat "brain" extracted out of `Workspace.tsx`
 * (issue #2507, continuing plan-001's `useChatSession` split).
 *
 * This is a REAL hook, not a thin prop-forwarder. It composes the
 * `useChatSession()` session (the reducer + hydration + queue-drain +
 * reattach poll, plan-001 Step 5.3) and additionally OWNS the co-located,
 * chat-scoped local state / effects / handlers that used to sit loose in the
 * Workspace body:
 *
 *   - `attachmentError` — the screenshot-paste attachment-cap rejection
 *     surfaced above the composer (#193). Owned here so both the `ui/error`
 *     agent-send effect (via the returned `setAttachmentError`) and the
 *     ChatPanel chip row read/clear the same state.
 *   - the "added to chat" confirmation cluster: `selectionAddedHint`
 *     (full-Preview tab pulse) + `attachmentPop` (in-editor composer
 *     thumbnail pop), their timer refs, the `flashSelectionAdded` /
 *     `flashAttachmentPop` callbacks, and the unmount cleanup effect.
 *   - `handleAddSelectionToChat` — the resize-then-attach handler used by the
 *     preview steering surface (and the protohub selection path). It reads the
 *     live workspace mode through a ref so it can stay a stable callback while
 *     still branching pulse-vs-pop by the mode at call time.
 *
 * BEHAVIOUR IS PRESERVED EXACTLY. Every branch, dependency array, ref, timer
 * duration, and the requestAnimationFrame re-arm below is a verbatim lift from
 * `Workspace.tsx`; the Workspace-owned inputs the brain reads (`gameId`,
 * `createInitialPrompt`, the kickoff setter + create-handoff dedupe ref, the
 * preview-toast setter, and the live `workspaceMode`) are passed in as
 * `UseChatModuleInput`, and the Workspace consumes the returned
 * `UseChatModule` object in place of the former inline reads. Nothing in the
 * render tree moves, so the no-remount guard (the `MountProbe` spec on
 * `WorkspaceChatSurface`) and the hydration / queue-drain timing are
 * untouched.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { InitialKickoffState } from '../workspace/run-state';
import type { WorkspaceMode } from '../workspace/workspace-view-types';
import { resizeImageFile } from './resize-image';
import { useChatSession, type UseChatSessionResult } from './useChatSession';

/** Toast setter shape — selection attach failures surface through it. */
export interface ChatModulePreviewToast {
  tone: 'info' | 'success' | 'error';
  message: string;
}

/**
 * Workspace-owned inputs the chat brain reads. These stay owned by the
 * Workspace (route params / kickoff watchdog / layout mode / toast) and are
 * threaded in so the brain can move without pulling the whole Workspace with
 * it.
 */
export interface UseChatModuleInput {
  gameId: string | undefined;
  createInitialPrompt: string | null;
  /** Build-kickoff watchdog state setter (Workspace-owned). */
  setInitialKickoff: Dispatch<SetStateAction<InitialKickoffState>>;
  /** Create-handoff dedupe ref (Workspace-owned; shared with route effects). */
  appliedCreateHandoffPromptKeyRef: MutableRefObject<string | null>;
  /** Preview/page toast setter — selection attach failures surface here. */
  setPreviewToast: (toast: ChatModulePreviewToast | null) => void;
  /** Live workspace mode — drives the pulse-vs-pop confirmation branch. */
  workspaceMode: WorkspaceMode;
}

/**
 * The typed surface the Workspace consumes. Spreads the full
 * `UseChatSessionResult` (so existing `chat` / `chatDispatch` / hydration
 * reads are unchanged) and adds the chat-owned attachment/selection state and
 * the add-selection handler.
 */
export interface UseChatModule extends UseChatSessionResult {
  /** Screenshot-paste attachment-cap rejection (#193), shown above the composer. */
  attachmentError: string | null;
  setAttachmentError: Dispatch<SetStateAction<string | null>>;
  /** Full-Preview "added to chat" tab pulse (5s). */
  selectionAddedHint: boolean;
  /** In-editor composer thumbnail pop (600ms, re-arms on rapid adds). */
  attachmentPop: boolean;
  /** Resize a selection image then attach it to the composer + flash confirmation. */
  handleAddSelectionToChat: (payload: { file: File; contextBlock: string }) => void;
}

export function useChatModule(input: UseChatModuleInput): UseChatModule {
  const {
    gameId,
    createInitialPrompt,
    setInitialKickoff,
    appliedCreateHandoffPromptKeyRef,
    setPreviewToast,
    workspaceMode,
  } = input;

  const session = useChatSession({
    gameId,
    createInitialPrompt,
    setInitialKickoff,
    appliedCreateHandoffPromptKeyRef,
  });
  const { chatDispatch } = session;

  /**
   * Screenshot-paste MVP (#193) — last attachment-cap rejection, shown above
   * the input until the user pastes a smaller image or dismisses. Cleared by
   * `setAttachmentError(null)` from the chip row's onDismiss + on every
   * successful add-attachment. Lives here (not in the pure chat reducer) so
   * the `ui/error` effect handler can populate it without touching the reducer
   * (the reducer is pure, so it can't dismiss the toast itself).
   */
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  // Transient "added to chat" confirmation: pulses the Develop tab + shows
  // a popover beneath it for 5s. Used only by the FULL Preview mode, where the
  // chat panel isn't on screen.
  const [selectionAddedHint, setSelectionAddedHint] = useState(false);
  const selectionHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashSelectionAdded = useCallback(() => {
    setSelectionAddedHint(true);
    if (selectionHintTimerRef.current) clearTimeout(selectionHintTimerRef.current);
    selectionHintTimerRef.current = setTimeout(() => setSelectionAddedHint(false), 5000);
  }, []);
  // In-editor (Develop split) confirmation: the attachment thumbnail above the
  // composer pops to highlight the just-attached selection — no tab popover.
  const [attachmentPop, setAttachmentPop] = useState(false);
  const attachmentPopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashAttachmentPop = useCallback(() => {
    setAttachmentPop(false);
    if (attachmentPopTimerRef.current) clearTimeout(attachmentPopTimerRef.current);
    // Re-arm on the next frame so the animation restarts even on rapid adds.
    requestAnimationFrame(() => {
      setAttachmentPop(true);
      attachmentPopTimerRef.current = setTimeout(() => setAttachmentPop(false), 600);
    });
  }, []);
  // Read the live workspace mode inside the (stable) add-selection callback.
  const workspaceModeRef = useRef(workspaceMode);
  workspaceModeRef.current = workspaceMode;
  useEffect(
    () => () => {
      if (selectionHintTimerRef.current) clearTimeout(selectionHintTimerRef.current);
      if (attachmentPopTimerRef.current) clearTimeout(attachmentPopTimerRef.current);
    },
    [],
  );

  const handleAddSelectionToChat = useCallback(
    (payload: { file: File; contextBlock: string }) => {
      void resizeImageFile(payload.file)
        .then((result) => {
          chatDispatch({
            type: 'user/add-attachment',
            attachment: {
              mimeType: result.mimeType,
              base64: result.base64,
              byteLength: result.byteLength,
              ...(result.didResize
                ? {
                    resizedFrom: {
                      width: result.originalWidth,
                      height: result.originalHeight,
                      finalWidth: result.finalWidth,
                      finalHeight: result.finalHeight,
                    },
                  }
                : {}),
            },
            selectionContext: payload.contextBlock,
          });
          // Full Preview mode → pulse the Develop tab (no chat visible).
          // In-editor (Develop split) → pop the composer thumbnail instead.
          if (workspaceModeRef.current === 'preview') {
            flashSelectionAdded();
          } else {
            flashAttachmentPop();
          }
        })
        .catch((err: unknown) => {
          console.error('[preview] selection attach failed', err);
          setPreviewToast({
            tone: 'error',
            message: "Couldn't attach that selection - try selecting again.",
          });
        });
    },
    [chatDispatch, flashSelectionAdded, flashAttachmentPop, setPreviewToast],
  );

  return {
    ...session,
    attachmentError,
    setAttachmentError,
    selectionAddedHint,
    attachmentPop,
    handleAddSelectionToChat,
  };
}
